import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TagChipsSelector from "@common/components/TagChipsSelector"
import TextM from "@common/components/text/TextM"
import { Toast } from "@common/components/card/Toast"
import { useRequestsBatch } from "@common/hooks/useRequestsBatch"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import { IS_WEB } from "@common/utils/mobileUtils"
import { theme } from "@common/theme/theme"
import { formIds } from "@data/constants/formConstants"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import React, { useEffect, useRef, useState } from "react"
import { Image, Platform, Pressable, View } from "react-native"
import { TextInput } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { useSelector } from "react-redux"
import { ISSUE_REPORT_CATEGORY } from "@data/constants/issueReportConstants"

const MAX_ATTACHMENTS = 3
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "video/mp4",
    "video/quicktime",
]

// Les clés viennent du contrat partagé : le libellé et l'emoji appartiennent à
// cet écran, la valeur envoyée au backend non.
const categories = [
    { key: ISSUE_REPORT_CATEGORY.AUDIO, label: "Audio", emoji: "🔊" },
    { key: ISSUE_REPORT_CATEGORY.VIDEO, label: "Vidéo", emoji: "📹" },
    { key: ISSUE_REPORT_CATEGORY.CONNECTION, label: "Connexion", emoji: "🔌" },
    { key: ISSUE_REPORT_CATEGORY.SCREEN_SHARE, label: "Partage d'écran", emoji: "🖥️" },
    { key: ISSUE_REPORT_CATEGORY.CONTENT, label: "Contenu / Énoncé", emoji: "📝" },
    { key: ISSUE_REPORT_CATEGORY.OTHER, label: "Autre", emoji: "❓" },
]

const isVideoFile = (type) =>
    type?.startsWith("video/") || type === "video/quicktime"

const LiveHelpModal = ({ visible, onDismiss, liveSessionId }) => {
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [description, setDescription] = useState("")
    const [descriptionError, setDescriptionError] = useState(false)
    const [attachments, setAttachments] = useState([])
    const [isDragging, setIsDragging] = useState(false)
    const webFileInputRef = useRef(null)

    const todayLiveSessionLogs = useSelector(
        (state) => state.liveMode?.todayLiveSessionLogs
    )

    const joinCount =
        todayLiveSessionLogs?.filter(
            (log) => log?.action === "joined_call"
        ).length ?? 0

    useEffect(() => {
        if (visible) {
            amplitude.track("bug_report.modal.opened")
        }
    }, [visible])

    const [globalProgress, submitted, startRequests] = useRequestsBatch(
        [],
        attachments,
        formIds.ISSUE_REPORT,
        {
            service: liveServiceV2.submitIssueReport,
            params: { id: liveSessionId },
            data: {
                category: selectedCategory,
                description: description,
                diagnostics: {
                    reconnections: Math.max(0, joinCount - 1),
                },
                platform: Platform.OS,
            },
            tmpFilesKey: "attachment_files",
            tmpFilesFormatter: (key) => key,
            additionalData: [],
            onSuccessBeforeNext: () => {
                amplitude.track("bug_report.submitted")
                onDismiss()
                resetForm()
                Toast.success({
                    message: "Signalement envoyé, merci !",
                    duration: 4000,
                    position: "top",
                })
                return true
            },
            onError: () => {
                onDismiss()
                resetForm()
            },
            showErrorDialog: true,
        },
        [],
        true
    )

    const resetForm = () => {
        setSelectedCategory(null)
        setDescription("")
        setDescriptionError(false)
        setAttachments([])
    }

    const handleDismiss = () => {
        onDismiss()
        resetForm()
    }

    const validateAndAddFiles = (files) => {
        const remaining = MAX_ATTACHMENTS - attachments.length
        if (remaining <= 0) return

        const validFiles = []
        for (const file of files) {
            if (validFiles.length >= remaining) break

            if (!ALLOWED_MIME_TYPES.includes(file.type)) {
                Toast.error({
                    message:
                        "Type de fichier non supporté. Utilisez JPEG, PNG, WEBP, MP4 ou MOV.",
                    duration: 4000,
                    position: "top",
                })
                continue
            }

            const fileSize = file.fileSize ?? file.size ?? 0
            if (fileSize > MAX_FILE_SIZE) {
                Toast.error({
                    message: "Fichier trop volumineux (max 20 Mo).",
                    duration: 4000,
                    position: "top",
                })
                continue
            }

            validFiles.push(file)
        }

        if (validFiles.length > 0) {
            setAttachments((prev) => [...prev, ...validFiles])
        }
    }

    const handlePickFiles = async () => {
        if (attachments.length >= MAX_ATTACHMENTS) return

        if (IS_WEB) {
            webFileInputRef.current?.click()
        } else {
            try {
                const ImagePicker = require("expo-image-picker")
                const { status } =
                    await ImagePicker.requestMediaLibraryPermissionsAsync()
                if (status !== "granted") {
                    Toast.error({
                        message: "Permission d'accès à la galerie requise.",
                        duration: 4000,
                        position: "top",
                    })
                    return
                }

                const result = await ImagePicker.launchImageLibraryAsync({
                    mediaTypes: ["images", "videos"],
                    allowsMultipleSelection: true,
                    selectionLimit: MAX_ATTACHMENTS - attachments.length,
                    quality: 0.8,
                })

                if (!result.canceled && result.assets) {
                    const files = result.assets.map((asset) => ({
                        uri: asset.uri,
                        name:
                            asset.fileName ||
                            `file_${Date.now()}.${asset.type === "video" ? "mp4" : "jpg"}`,
                        type:
                            asset.mimeType ||
                            (asset.type === "video"
                                ? "video/mp4"
                                : "image/jpeg"),
                        fileSize: asset.fileSize,
                    }))
                    validateAndAddFiles(files)
                }
            } catch (e) {
                console.warn("Image picker error:", e)
            }
        }
    }

    const handleWebFileChange = (event) => {
        const fileList = event.target.files
        if (!fileList) return

        const files = Array.from(fileList).map((file) => ({
            uri: URL.createObjectURL(file),
            name: file.name,
            type: file.type,
            size: file.size,
        }))
        validateAndAddFiles(files)

        // Reset input so the same file can be picked again
        event.target.value = ""
    }

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index))
    }

    const handleSubmit = () => {
        if (!selectedCategory) return

        if (!description.trim()) {
            setDescriptionError(true)
            return
        }

        setDescriptionError(false)
        startRequests()
    }

    const canAddMore = attachments.length < MAX_ATTACHMENTS

    const handleDrop = (event) => {
        event.preventDefault()
        setIsDragging(false)
        if (!canAddMore) return

        const fileList = event.dataTransfer?.files
        if (!fileList) return

        const files = Array.from(fileList).map((file) => ({
            uri: URL.createObjectURL(file),
            name: file.name,
            type: file.type,
            size: file.size,
        }))
        validateAndAddFiles(files)
    }

    const handleDragOver = (event) => {
        event.preventDefault()
        if (canAddMore) setIsDragging(true)
    }

    const handleDragLeave = (event) => {
        event.preventDefault()
        setIsDragging(false)
    }

    return (
        <ModalSkeleton
            isVisible={visible}
            onClose={handleDismiss}
            title="Signaler un problème"
        >
            <View style={{ gap: 16 }}>
                <TextM fontSize={14} color={theme.colors.grey}>
                    Quel type de problème rencontres-tu ?
                </TextM>

                <TagChipsSelector
                    tags={categories}
                    selectedTags={selectedCategory ? [selectedCategory] : []}
                    onTagToggle={(key) =>
                        setSelectedCategory(
                            selectedCategory === key ? null : key
                        )
                    }
                />

                <TextInput
                    label="Description"
                    value={description}
                    onChangeText={(text) => {
                        setDescription(text)
                        if (descriptionError && text.trim()) {
                            setDescriptionError(false)
                        }
                    }}
                    multiline
                    numberOfLines={3}
                    maxLength={1000}
                    placeholder="Décris le problème en quelques mots..."
                    mode="outlined"
                    outlineColor={
                        descriptionError
                            ? theme.colors.error
                            : theme.colors.light
                    }
                    activeOutlineColor={
                        descriptionError
                            ? theme.colors.error
                            : theme.colors.light
                    }
                    style={{ fontSize: 16, fontFamily: "Montserrat" }}
                    error={descriptionError}
                />
                {descriptionError && (
                    <TextM
                        fontSize={12}
                        color={theme.colors.error}
                        style={{ marginTop: -12 }}
                    >
                        La description est obligatoire.
                    </TextM>
                )}

                {/* File picker with drag & drop (web) */}
                {IS_WEB ? (
                    <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => canAddMore && handlePickFiles()}
                        style={{
                            borderWidth: 2,
                            borderStyle: "dashed",
                            borderColor: isDragging
                                ? theme.colors.primary
                                : canAddMore
                                  ? theme.colors.grey
                                  : theme.colors.light,
                            borderRadius: 8,
                            padding: 16,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            opacity: canAddMore ? 1 : 0.5,
                            backgroundColor: isDragging
                                ? theme.colors.primaryLighter
                                : "transparent",
                            cursor: canAddMore ? "pointer" : "default",
                            transition: "background-color 0.2s, border-color 0.2s",
                        }}
                    >
                        <FontAwesome5
                            name={isDragging ? "download" : "cloud-upload-alt"}
                            size={20}
                            color={
                                isDragging
                                    ? theme.colors.primary
                                    : canAddMore
                                      ? theme.colors.grey
                                      : theme.colors.light
                            }
                        />
                        <TextM
                            fontSize={14}
                            color={
                                isDragging
                                    ? theme.colors.primary
                                    : canAddMore
                                      ? theme.colors.grey
                                      : theme.colors.light
                            }
                        >
                            {isDragging
                                ? "Dépose tes fichiers ici"
                                : "Glisser-déposer ou cliquer pour ajouter"}
                        </TextM>
                        <TextM
                            fontSize={11}
                            color={theme.colors.light}
                        >
                            JPEG, PNG, WEBP, MP4, MOV — max 20 Mo
                        </TextM>
                    </div>
                ) : (
                    <Pressable
                        onPress={handlePickFiles}
                        disabled={!canAddMore}
                        style={{
                            borderWidth: 1,
                            borderStyle: "dashed",
                            borderColor: canAddMore
                                ? theme.colors.grey
                                : theme.colors.light,
                            borderRadius: 8,
                            padding: 12,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            opacity: canAddMore ? 1 : 0.5,
                        }}
                    >
                        <FontAwesome5
                            name="paperclip"
                            size={14}
                            color={
                                canAddMore
                                    ? theme.colors.grey
                                    : theme.colors.light
                            }
                        />
                        <TextM
                            fontSize={14}
                            color={
                                canAddMore
                                    ? theme.colors.grey
                                    : theme.colors.light
                            }
                        >
                            Ajouter des fichiers
                        </TextM>
                    </Pressable>
                )}

                {/* Hidden web file input */}
                {IS_WEB && (
                    <input
                        ref={webFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
                        multiple
                        onChange={handleWebFileChange}
                        style={{ display: "none" }}
                    />
                )}

                {/* Thumbnails */}
                {attachments.length > 0 && (
                    <View
                        style={{
                            flexDirection: "row",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        {attachments.map((attachment, index) => (
                            <View
                                key={index}
                                style={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 8,
                                    overflow: "hidden",
                                    backgroundColor: theme.colors.lightMore,
                                }}
                            >
                                {isVideoFile(attachment.type) ? (
                                    <View
                                        style={{
                                            flex: 1,
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor:
                                                theme.colors.lightMore,
                                        }}
                                    >
                                        <FontAwesome5
                                            name="video"
                                            size={24}
                                            color={theme.colors.grey}
                                        />
                                    </View>
                                ) : (
                                    <Image
                                        source={{ uri: attachment.uri }}
                                        style={{
                                            width: 72,
                                            height: 72,
                                        }}
                                        resizeMode="cover"
                                    />
                                )}
                                <Pressable
                                    onPress={() => removeAttachment(index)}
                                    style={{
                                        position: "absolute",
                                        top: 2,
                                        right: 2,
                                        backgroundColor: "rgba(0,0,0,0.6)",
                                        borderRadius: 10,
                                        width: 20,
                                        height: 20,
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <FontAwesome5
                                        name="times"
                                        size={10}
                                        color="white"
                                    />
                                </Pressable>
                            </View>
                        ))}
                    </View>
                )}

                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        backgroundColor: theme.colors.primaryLighter,
                        borderRadius: 8,
                        padding: 12,
                        gap: 8,
                    }}
                >
                    <FontAwesome5
                        name="info-circle"
                        size={14}
                        color={theme.colors.primary}
                    />
                    <TextM
                        fontSize={12}
                        color={theme.colors.primary}
                        style={{ flex: 1 }}
                        wrap
                    >
                        Des informations techniques seront automatiquement
                        jointes pour aider à résoudre le problème.
                    </TextM>
                </View>

                <FilledButtonM
                    label="Envoyer le signalement"
                    onPress={handleSubmit}
                    loading={submitted}
                    disabled={
                        !selectedCategory || !description.trim() || submitted
                    }
                    style={{ width: null }}
                    color={theme.colors.primary}
                />
            </View>
        </ModalSkeleton>
    )
}

export default LiveHelpModal
